import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityAdapter,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit'
import { RootState } from '../store'
import { LineItemTab } from '../../components/sale/sale-line-item/LineItemTabs'

type LineItemInputError = {
  id: string
  error: string | null
}

export const lineItemQuantityInputErrors = createEntityAdapter<LineItemInputError>()
export const lineItemPriceInputErrors = createEntityAdapter<LineItemInputError>()
export const lineItemDiscountInputErrors = createEntityAdapter<LineItemInputError>()
export const lineItemNoteError = createEntityAdapter<LineItemInputError>()
export const lineItemFulfilmentNoteError = createEntityAdapter<LineItemInputError>()

export interface ReduxUIState {
  /** The ID of the line item that is expanded right now */
  expandedLineItemId?: string
  /** The current tab open for the expanded line item */
  lineItemCurrentTab: LineItemTab
  /** The line item ID for which the fulfilment popover is open */
  openFulfilmentPopoverLineItemId: string
  /** The line item quantity input error */
  lineItemQuantityInputErrors: EntityState<LineItemInputError>
  /** The line item price input errors */
  lineItemPriceInputErrors: EntityState<LineItemInputError>
  /** The line item discount input errors */
  lineItemDiscountInputErrors: EntityState<LineItemInputError>
  /** The line item note input errors */
  lineItemNoteErrors: EntityState<LineItemInputError>
  /** The line item fulfilment note input errors */
  lineItemFulfilmentNoteErrors: EntityState<LineItemInputError>
  /** The sale customer contact info reviewed status */
  isSaleCustomerContactInfoReviewed: boolean
  /** The sale data is being updated before transition to payments */
  isFinalisingSale: boolean
  /** Waiting for workflows to complete */
  isWaitingForWorkflows: boolean
  /** The query string for the customer search */
  customerSearchQuery: string
  /** The user is returning a line item
   * but it doesn't have permission to do so **/
  isAReturnWithoutPermission: boolean
  /** When the payment button is pressed, but we are waiting on a sale calculation
   * before transitioning to the payment screen. */
  isTransitioningToPaymentScreen: boolean
  /** When the lsPay button is pressed, but we are waiting on a sale calculation
   * before transitioning to the payment screen. */
  isTransitioningToPaymentScreenViaLsPay: boolean
  /** Whether to show the training mode modal when entering training mode */
  showTrainingModeModal: boolean
  processingAddPayment: boolean
}

export const initialState: ReduxUIState = {
  expandedLineItemId: '',
  lineItemCurrentTab: LineItemTab.DETAIL,
  openFulfilmentPopoverLineItemId: '',
  lineItemQuantityInputErrors: lineItemQuantityInputErrors.getInitialState(),
  lineItemPriceInputErrors: lineItemPriceInputErrors.getInitialState(),
  lineItemDiscountInputErrors: lineItemDiscountInputErrors.getInitialState(),
  lineItemNoteErrors: lineItemNoteError.getInitialState(),
  lineItemFulfilmentNoteErrors: lineItemFulfilmentNoteError.getInitialState(),
  isSaleCustomerContactInfoReviewed: false,
  isFinalisingSale: false,
  isWaitingForWorkflows: false,
  customerSearchQuery: '',
  isAReturnWithoutPermission: false,
  isTransitioningToPaymentScreen: false,
  isTransitioningToPaymentScreenViaLsPay: false,
  showTrainingModeModal: false,
  processingAddPayment: false,
}

const uiStateSlice = createSlice({
  name: 'uiState',
  initialState,
  reducers: {
    setExpandedLineItemId(state, action: PayloadAction<string>) {
      state.expandedLineItemId = action.payload
      state.lineItemCurrentTab = LineItemTab.DETAIL
      state.openFulfilmentPopoverLineItemId = ''
    },

    setExpandedLineItemIdWithFulfilmentTab(
      state,
      action: PayloadAction<string>
    ) {
      state.expandedLineItemId = action.payload
      state.lineItemCurrentTab = LineItemTab.FULFILMENT
      state.openFulfilmentPopoverLineItemId = ''
    },

    setExpandedLineItemIdWithFulfilmentTabPreservingPopover(
      state,
      action: PayloadAction<string>
    ) {
      state.expandedLineItemId = action.payload
      state.lineItemCurrentTab = LineItemTab.FULFILMENT
      // Don't clear openFulfilmentPopoverLineItemId to preserve popover context
    },

    resetExpandedLineItemId(state) {
      state.expandedLineItemId = ''
    },

    setLineItemCurrentTab(state, action: PayloadAction<LineItemTab>) {
      state.lineItemCurrentTab = action.payload
    },

    openFulfilmentLineItemPopover(state, action: PayloadAction<string>) {
      state.openFulfilmentPopoverLineItemId = action.payload
    },

    setLineItemQuantityInputError(
      state,
      action: PayloadAction<
        Partial<LineItemInputError> & Pick<LineItemInputError, 'id'>
      >
    ) {
      setLineItemInputError(
        state.lineItemQuantityInputErrors,
        action,
        lineItemQuantityInputErrors
      )
    },

    setIsAReturnWithoutPermission(state, action: PayloadAction<boolean>) {
      state.isAReturnWithoutPermission = action.payload.valueOf()
    },

    removeLineItemQuantityInputError(state, action: PayloadAction<string>) {
      lineItemQuantityInputErrors.removeOne(
        state.lineItemQuantityInputErrors,
        action.payload
      )
    },

    resetLineItemInputErrors(state) {
      state.lineItemQuantityInputErrors = lineItemQuantityInputErrors.getInitialState()
      state.lineItemPriceInputErrors = lineItemPriceInputErrors.getInitialState()
      state.lineItemDiscountInputErrors = lineItemDiscountInputErrors.getInitialState()
      state.lineItemNoteErrors = lineItemNoteError.getInitialState()
      state.lineItemFulfilmentNoteErrors = lineItemFulfilmentNoteError.getInitialState()
    },

    markSaleCustomerContactInfoAsReviewed(state) {
      state.isSaleCustomerContactInfoReviewed = true
    },

    resetSaleCustomerContactInfoReviewStatus(state) {
      state.isSaleCustomerContactInfoReviewed = false
    },

    setLineItemPriceInputError(
      state,
      action: PayloadAction<
        Partial<LineItemInputError> & Pick<LineItemInputError, 'id'>
      >
    ) {
      setLineItemInputError(
        state.lineItemPriceInputErrors,
        action,
        lineItemPriceInputErrors
      )
    },

    removeLineItemPriceInputError(state, action: PayloadAction<string>) {
      lineItemPriceInputErrors.removeOne(
        state.lineItemPriceInputErrors,
        action.payload
      )
    },
    setLineItemDiscountInputError(
      state,
      action: PayloadAction<
        Partial<LineItemInputError> & Pick<LineItemInputError, 'id'>
      >
    ) {
      setLineItemInputError(
        state.lineItemDiscountInputErrors,
        action,
        lineItemDiscountInputErrors
      )
    },
    removeLineItemDiscountInputError(state, action: PayloadAction<string>) {
      lineItemDiscountInputErrors.removeOne(
        state.lineItemDiscountInputErrors,
        action.payload
      )
    },
    removeLineItemInputErrors(state, action: PayloadAction<string>) {
      lineItemQuantityInputErrors.removeOne(
        state.lineItemQuantityInputErrors,
        action.payload
      )
      lineItemPriceInputErrors.removeOne(
        state.lineItemPriceInputErrors,
        action.payload
      )
      lineItemDiscountInputErrors.removeOne(
        state.lineItemDiscountInputErrors,
        action.payload
      )
      lineItemNoteError.removeOne(state.lineItemNoteErrors, action.payload)
      lineItemFulfilmentNoteError.removeOne(
        state.lineItemFulfilmentNoteErrors,
        action.payload
      )
    },
    finalisingSale(state) {
      state.isFinalisingSale = true
    },
    finalisingSaleComplete(state) {
      state.isFinalisingSale = false
    },
    workflowsStarted(state) {
      state.isWaitingForWorkflows = true
    },
    workflowsComplete(state) {
      state.isWaitingForWorkflows = false
    },
    setCustomerSearchQuery(state, action: PayloadAction<string>) {
      state.customerSearchQuery = action.payload
    },
    setLineItemNoteError(
      state,
      action: PayloadAction<
        Partial<LineItemInputError> & Pick<LineItemInputError, 'id'>
      >
    ) {
      setLineItemInputError(state.lineItemNoteErrors, action, lineItemNoteError)
    },
    setLineItemFulfilmentNoteError(
      state,
      action: PayloadAction<
        Partial<LineItemInputError> & Pick<LineItemInputError, 'id'>
      >
    ) {
      setLineItemInputError(
        state.lineItemFulfilmentNoteErrors,
        action,
        lineItemFulfilmentNoteError
      )
    },
    setIsTransitioningToPaymentScreen(state, action: PayloadAction<boolean>) {
      state.isTransitioningToPaymentScreen = action.payload
    },
    setIsTransitioningToPaymentScreenViaLsPay(
      state,
      action: PayloadAction<boolean>
    ) {
      state.isTransitioningToPaymentScreenViaLsPay = action.payload
    },
    setShowTrainingModeModal(state, action: PayloadAction<boolean>) {
      state.showTrainingModeModal = action.payload
    },
    startProcessingAddPayment(state) {
      state.processingAddPayment = true
    },
    finishedProcessingAddPayment(state) {
      state.processingAddPayment = false
    },
  },
})

function setLineItemInputError(
  errorState: EntityState<LineItemInputError>,
  action: PayloadAction<
    Partial<LineItemInputError> & Pick<LineItemInputError, 'id'>
  >,
  adapter: EntityAdapter<LineItemInputError>
) {
  if (!errorState.ids.includes(action.payload.id)) {
    adapter.addOne(errorState, {
      id: action.payload.id,
      error: action.payload.error || null,
    })
  } else {
    adapter.updateOne(errorState, {
      id: action.payload.id,
      changes: {
        ...(action.payload.error !== undefined
          ? { error: action.payload.error }
          : {}),
      },
    })
  }
}

export const { reducer } = uiStateSlice

export const {
  setExpandedLineItemId,
  setExpandedLineItemIdWithFulfilmentTab,
  setExpandedLineItemIdWithFulfilmentTabPreservingPopover,
  resetExpandedLineItemId,
  setLineItemCurrentTab,
  setIsAReturnWithoutPermission,
  openFulfilmentLineItemPopover,
  setLineItemQuantityInputError,
  removeLineItemQuantityInputError,
  resetLineItemInputErrors,
  markSaleCustomerContactInfoAsReviewed,
  resetSaleCustomerContactInfoReviewStatus,
  setLineItemPriceInputError,
  removeLineItemPriceInputError,
  setLineItemDiscountInputError,
  removeLineItemDiscountInputError,
  removeLineItemInputErrors,
  finalisingSale,
  finalisingSaleComplete,
  workflowsStarted,
  workflowsComplete,
  setCustomerSearchQuery,
  setLineItemNoteError,
  setLineItemFulfilmentNoteError,
  setIsTransitioningToPaymentScreen,
  setIsTransitioningToPaymentScreenViaLsPay,
  setShowTrainingModeModal,
  startProcessingAddPayment,
  finishedProcessingAddPayment,
} = uiStateSlice.actions

export const selectUIState = (state: RootState) => state.uiState
export const selectExpandedLineItemId = (state: RootState) =>
  state.uiState.expandedLineItemId
export const selectLineItemCurrentTab = (state: RootState) =>
  state.uiState.lineItemCurrentTab
export const selectOpenFulfilmentPopoverLineItemId = (state: RootState) =>
  state.uiState.openFulfilmentPopoverLineItemId
export const selectIsFulfilmentPopoverOpen = (state: RootState) =>
  state.uiState.openFulfilmentPopoverLineItemId !== ''

export const selectLineItemQuantityInputError = (lineItemId: string) => (
  state: RootState
) =>
  lineItemQuantityInputErrors
    .getSelectors()
    .selectById(state.uiState.lineItemQuantityInputErrors, lineItemId)?.error ||
  null

export const selectHasQuantityInputError = (state: RootState) => {
  const inputErrors = lineItemQuantityInputErrors
    .getSelectors()
    .selectAll(state.uiState.lineItemQuantityInputErrors)

  return inputErrors.some(inputError => inputError.error !== null)
}

export const selectIsSaleCustomerContactInfoReviewed = (state: RootState) =>
  state.uiState.isSaleCustomerContactInfoReviewed

export const selectLineItemPriceInputError = (lineItemId: string) => (
  state: RootState
) =>
  lineItemPriceInputErrors
    .getSelectors()
    .selectById(state.uiState.lineItemPriceInputErrors, lineItemId)?.error ||
  null

export const selectHasPriceInputError = (state: RootState) => {
  const inputErrors = lineItemPriceInputErrors
    .getSelectors()
    .selectAll(state.uiState.lineItemPriceInputErrors)

  return inputErrors.some(inputError => inputError.error !== null)
}

export const selectLineItemDiscountError = (lineItemId: string) => (
  state: RootState
) =>
  lineItemDiscountInputErrors
    .getSelectors()
    .selectById(state.uiState.lineItemDiscountInputErrors, lineItemId)?.error ||
  null

export const selectHasDiscountError = (state: RootState) => {
  const inputErrors = lineItemDiscountInputErrors
    .getSelectors()
    .selectAll(state.uiState.lineItemDiscountInputErrors)

  return inputErrors.some(inputError => inputError.error !== null)
}

export const selectLineItemNoteError = (lineItemId: string) => (
  state: RootState
) =>
  lineItemNoteError
    .getSelectors()
    .selectById(state.uiState.lineItemNoteErrors, lineItemId)?.error || null

export const selectHasLineItemNoteError = (state: RootState) => {
  const inputErrors = lineItemNoteError
    .getSelectors()
    .selectAll(state.uiState.lineItemNoteErrors)

  return inputErrors.some(inputError => inputError.error !== null)
}

export const selectSingleLineItemHasErrors = (lineItemId: string) => (
  state: RootState
) =>
  Boolean(
    selectLineItemNoteError(lineItemId)(state) ||
      selectLineItemFulfilmentNoteError(lineItemId)(state) ||
      selectLineItemPriceInputError(lineItemId)(state) ||
      selectLineItemQuantityInputError(lineItemId)(state) ||
      selectLineItemDiscountError(lineItemId)(state)
  )

// selectIsOnlyLineItemQuantityInputError returns true if the only error is a line item quantity input error
export const selectIsOnlyLineItemQuantityInputError = (lineItemId: string) => (
  state: RootState
) =>
  Boolean(
    selectLineItemQuantityInputError(lineItemId)(state) &&
      !selectLineItemPriceInputError(lineItemId)(state) &&
      !selectLineItemDiscountError(lineItemId)(state) &&
      !selectLineItemNoteError(lineItemId)(state) &&
      !selectLineItemFulfilmentNoteError(lineItemId)(state)
  )

export const selectLineItemFulfilmentNoteError = (lineItemId: string) => (
  state: RootState
) =>
  lineItemFulfilmentNoteError
    .getSelectors()
    .selectById(state.uiState.lineItemFulfilmentNoteErrors, lineItemId)
    ?.error || null

export const selectHasLineItemFulfilmentNoteError = (state: RootState) =>
  lineItemFulfilmentNoteError
    .getSelectors()
    .selectAll(state.uiState.lineItemFulfilmentNoteErrors)
    .some(inputError => inputError.error !== null)

export const selectHasLineItemError = createSelector(
  [
    selectHasQuantityInputError,
    selectHasPriceInputError,
    selectHasDiscountError,
    selectHasLineItemNoteError,
    selectHasLineItemFulfilmentNoteError,
  ],
  (
    hasQuantityError,
    hasPriceError,
    hasDiscountError,
    hasLineItemNoteError,
    hasLineItemFulfilmentNoteError
  ) =>
    hasPriceError ||
    hasQuantityError ||
    hasDiscountError ||
    hasLineItemNoteError ||
    hasLineItemFulfilmentNoteError
)

export const selectIsFinalisingSale = createSelector(
  [(state: RootState) => state.uiState.isFinalisingSale],
  isFinalisingSale => isFinalisingSale
)

export const selectIsWaitingForWorkflows = createSelector(
  [(state: RootState) => state.uiState.isWaitingForWorkflows],
  isWaitingForWorkflows => isWaitingForWorkflows
)

export const selectIsAReturnWithoutPermission = createSelector(
  [(state: RootState) => state.uiState.isAReturnWithoutPermission],
  isAReturnWithoutPermission => isAReturnWithoutPermission
)

export const selectCustomersearchQuery = (state: RootState) =>
  state.uiState.customerSearchQuery

export const selectIsTransitioningToPaymentScreen = (state: RootState) =>
  state.uiState.isTransitioningToPaymentScreen

export const selectIsTransitioningToPaymentScreenViaLsPay = (
  state: RootState
) => state.uiState.isTransitioningToPaymentScreenViaLsPay

export const selectProcessingAddPayment = (state: RootState) =>
  state.uiState.processingAddPayment
